import axiosInstance from '../../config/axiosInstance';
import { UserBalance, User as UserBalanceType } from '../../common/interfaces/userBalance';
import { IHistoryResponse } from '../../pages/UserHistory/types';
import { UserOrganizationV2 } from '../../types/dashboardV2';
import { User, UserCreateRequest, UserUpdateRequest } from '../../types/UserTypes';

export interface IUser {
  _id: string;
  email?: string;
  phone?: string;
  status?: string;
  balance?: number;
  role?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IUserResponse extends IHistoryResponse {
  data: IUser[];
}

export class UserService {
  private readonly USER_BALANCE_PATH = '/ocpp/user-balance';
  private readonly USER_PATH = '/users/user';
  private readonly USERS_PATH = '/users';

  async getUserBalanceAndStatus(): Promise<UserBalance> {
    return await axiosInstance.get(this.USER_BALANCE_PATH);
  }

  async getUser(id?: string): Promise<User> {
    const path = id ? `${this.USER_PATH}/${id}` : this.USER_PATH;
    return await axiosInstance.get(path);
  }

  async getUserBalance(id?: string): Promise<UserBalanceType> {
    const path = id ? `${this.USER_PATH}/${id}` : this.USER_PATH;
    return await axiosInstance.get(path);
  }

  async getUsers(
    search: Record<string, string | number | boolean | undefined>,
    page: number = 1,
    pageSize: number = 10,
    sort: string | null = '',
  ) {
    const response: IUserResponse = await axiosInstance.get(this.USERS_PATH, {
      params: { page, pageSize, sort, ...search },
    });

    return response;
  }

  async updateUser(id: string, data: UserUpdateRequest): Promise<User> {
    return await axiosInstance.put(`${this.USERS_PATH}/${id}`, data);
  }

  async deleteUser(id: string): Promise<void> {
    return await axiosInstance.delete(`${this.USERS_PATH}/${id}`);
  }

  async createUser(data: UserCreateRequest): Promise<User> {
    return await axiosInstance.post(this.USERS_PATH, data);
  }

  async getUserById(id: string): Promise<
    IUser & {
      useBiometric?: boolean | null;
      roleIds?: Array<string | { _id?: string; id?: string; name?: string }>;
      notification?: { telegram_chat_id?: string | null };
      organization?: UserOrganizationV2 | null;
      canJoinOrganization?: boolean;
    }
  > {
    return await axiosInstance.get(`${this.USERS_PATH}/${id}`);
  }

  async updateUserBalance(
    id: string,
    payload: { amount: number; mode: 'set' | 'adjust'; reason?: string },
  ): Promise<IUser> {
    return await axiosInstance.patch(
      `${this.USERS_PATH}/${id}/balance`,
      payload,
    );
  }

  async updateUserStatus(
    id: string,
    payload: { status: 'active' | 'inactive' | 'blocked' },
  ): Promise<IUser> {
    return await axiosInstance.patch(
      `${this.USERS_PATH}/${id}/status`,
      payload,
    );
  }

  async mergeAccounts(survivorId: string, loserId: string): Promise<IUser> {
    return await axiosInstance.post(`${this.USERS_PATH}/${survivorId}/merge`, {
      loserId,
    });
  }
}

export const userService = new UserService();